import "./contact-modal.scss";

import { Close } from "@mui/icons-material";
import {
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  DialogProps,
  Grid,
  IconButton,
} from "@mui/material";
import Modal from "../modal/modal";
import Typography from "../typography/typography";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import Button from "../button/button";
import { Copy } from "../icons";

import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormLoader } from "../loaders";
import DynamicInput from "../form-control/form-builder/form-builder-element/text";
import DynamicPhone from "../form-control/form-builder/form-builder-element/phone";
import { CountryData } from "react-phone-input-2";
import { useTranslation } from "react-i18next";

interface IFormInput {
  contacts: {
    name?: string;
    email?: string;
    contact_no?: string;
    country_code?: string;
  };
}

interface ContactModalProps {
  isOpen: boolean;
  setIsOpen: () => void;
  modalProps?: DialogProps;
  itemData?: any;
  itemIndex?: number;
  onSave: (data: any, itemIndex?: number) => void;
  translationKey: string;
}

const validationSchema = Yup.object().shape({
  contacts: Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email format').required('Email is required'),
  }),
});

const ContactModal: React.FC<ContactModalProps> = ({
  isOpen,
  setIsOpen,
  modalProps,
  itemData,
  itemIndex,
  onSave,
  translationKey,
}) => {
  const { t } = useTranslation();
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const defaultValues = {
    contacts: {
      name: '',
      email: '',
      contact_no: '',
      country_code: '',
    }
  };

  const { control, handleSubmit, setValue, reset, getValues, trigger, watch } =
    useForm<IFormInput>({
      resolver: yupResolver(validationSchema),
      mode: "all",
    });

    const onSubmit = async (values: IFormInput) => {
      try {
        onSave(values, itemIndex);
        handleModalClose();
      } catch (error) {
        console.error("Error saving contact:", error);
      }
    };

    const handleDuplicate = async () => {
      try {
        const isValid = await trigger();
        if (isValid) {
          setSubmitLoading(true);
          const values = getValues();
          await onSave(values);
          handleModalClose();
        }
      } catch (error) {
        console.error("Error duplicating contact:", error);
      } finally {
        setSubmitLoading(false);
      }
    };

  const handleModalClose = () => {
    setIsOpen();
  };

  const handleSetCountryCodeField = (country: CountryData) => {
    const values = getValues();
    const oldVal = values?.contacts?.country_code;
    const newVal = `+${country.dialCode}`;
    if (oldVal !== newVal) {
      setValue(`contacts.country_code` as never, newVal as never);
    }
  };

  useEffect(() => {
    if (itemData) {
      reset({ contacts: itemData });
    }
  }, [itemData, reset]);

  return (
    <Modal
      open={isOpen}
      onClose={handleModalClose}
      maxWidth="md"
      fullWidth
      {...modalProps}
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {submitLoading && <FormLoader />}
        <DialogTitle className="contactModal--Title">
          <Typography type="s3" color="inherit" weight="medium">
            {t(`accounting.${translationKey}.contactModal.title`)}
          </Typography>
          <IconButton onClick={handleModalClose}>
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Grid container rowGap={1.5} columnSpacing={3} pt={3}>
            <Grid item sm={6}>
              <DynamicInput
                type="text"
                formControl={control}
                label={t(`accounting.${translationKey}.contactModal.name_label`)}
                name="name"
                fieldArrayName="contacts"
                placeholder={t(`accounting.${translationKey}.contactModal.name_placeholder`)}
                required
              />
            </Grid>
            <Grid item sm={6}>
              <DynamicInput
                type="email"
                formControl={control}
                label={t(`accounting.${translationKey}.contactModal.email_label`)}
                name="email"
                fieldArrayName="contacts"
                placeholder={t(`accounting.${translationKey}.contactModal.email_placeholder`)}
                required
              />
            </Grid>
            <Grid item sm={6}>
              <DynamicPhone
                type="text"
                formControl={control}
                label={t(`accounting.${translationKey}.contactModal.contact_no_label`)}
                name="contact_no"
                fieldArrayName="contacts"
                setCountryCodeField={(country: CountryData) =>
                  handleSetCountryCodeField(country)
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions className={`contactModal--ActionContainer ${!itemData && 'contactModal--ActionContainer--FlexEnd'}`}>
          {itemData &&
            <Button
              className="contactModal--ActionContainer--WhiteBtn"
              startIcon={<Copy />}
              onClick={handleDuplicate}
              disabled={submitLoading}
              type="button"
            >
              {t("common.duplicate")}
            </Button>
          }
          <Box display="flex" gap={1.5}>
            <Button
              className="contactModal--ActionContainer--WhiteBtn"
              onClick={handleModalClose}
              disabled={submitLoading}
              type="button"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              endIcon={
                submitLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : undefined
              }
              disabled={submitLoading}
            >
              {t("common.save")}
            </Button>
          </Box>
        </DialogActions>
      </form>
    </Modal>
  );
};

export default ContactModal;
